/**
 * WHO-5761: the intent word in the `<h1>` of the vintage style pages.
 *
 * The service titles a style x category page "Vintage Banken" in every locale and the hub just
 * "Vintage". Search intent differs per market: NL, DE and EN search "vintage <noun>", France
 * searches "<nom> vintage" and "<nom> d'occasion" (the Selency wording). So the hub gets a noun,
 * FR gets the noun first plus "et d'occasion", and a bare noun title gains the "Vintage" prefix.
 * Anything that is not a vintage style page keeps the service title untouched.
 */

const VINTAGE_HUB_PATH = /^\/style\/vintage\/?$/;
const VINTAGE_CATEGORY_PATH = /^\/style\/vintage\/[^/]+\/?$/;
const VINTAGE_PREFIX = /^vintage\s+/i;
const VINTAGE_WORD = /\bvintage\b/i;

const HUB_HEADINGS: Record<string, string> = {
  en: "Vintage furniture and design",
  nl: "Vintage meubels en design",
  de: "Vintage Möbel und Design",
  fr: "Meubles vintage et d'occasion",
  es: "Muebles vintage",
  it: "Mobili vintage",
};

const FR_OCCASION_SUFFIX = " vintage et d'occasion";

/** Locales whose heading keeps the "Vintage <noun>" order. */
const PREFIX_LOCALES = new Set(["en", "nl", "de"]);

const capitalize = (text: string) =>
  text.charAt(0).toLocaleUpperCase("fr") + text.slice(1);

export const buildIntentHeading = (
  title: string,
  path: string | undefined,
  locale: string | undefined
): string => {
  if (!path || !locale) {
    return title;
  }

  if (VINTAGE_HUB_PATH.test(path)) {
    return HUB_HEADINGS[locale] ?? title;
  }

  if (!VINTAGE_CATEGORY_PATH.test(path)) {
    return title;
  }

  if (locale === "fr") {
    const noun = title.replace(VINTAGE_PREFIX, "").trim();

    return noun && !VINTAGE_WORD.test(noun)
      ? `${capitalize(noun)}${FR_OCCASION_SUFFIX}`
      : title;
  }

  if (PREFIX_LOCALES.has(locale) && !VINTAGE_WORD.test(title)) {
    return `Vintage ${title}`;
  }

  return title;
};
